'use client';

import { forwardRef, useContext } from 'react';

import { css, cx } from '@/styled-system/css';

import { FormFieldContext } from '../form-field/context/form-field.context';
import { ProgressRootContext } from './context/progress-root.context';
import { progressBarRecipe } from './styles/progress-bar.styles';
import type { ProgressBarContainerPropsInterface } from './types/progress-bar.types';

/**
 * The progress bar's container
 *
 * @related ['ProgressBar']
 * @since 10.0.0
 */
export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarContainerPropsInterface>(
  (props: ProgressBarContainerPropsInterface, forwardedRef) => {
    const formFieldContext = useContext(FormFieldContext);
    const context = useContext(ProgressRootContext);

    const {
      'aria-busy': ariaBusyProp,
      'aria-describedby': ariaDescribedby = context['aria-describedby'] ||
        formFieldContext.descriptionId,
      'aria-label': ariaLabel = context['aria-label'],
      'aria-labelledby': ariaLabelledby = context['aria-labelledby'] || formFieldContext.labelId,
      children,
      className,
      css: cssProp,
      minValue = context.minValue || 0,
      maxValue = context.maxValue || 100,
      size = context.size || 'sm',
      value = context.value || 0,
      ...rest
    } = props;

    const ariaBusy = ariaBusyProp ?? value < maxValue;

    const styles = css(progressBarRecipe.raw({ size }), cssProp);

    return (
      <div
        role="progressbar"
        aria-busy={ariaBusy}
        aria-describedby={ariaDescribedby}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledby}
        aria-valuenow={value}
        aria-valuemin={minValue}
        aria-valuemax={maxValue}
        data-c11n-component="Progress.Bar"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', 'c11n_progress__bar', styles, className)}
        ref={forwardedRef}
        {...rest}
      >
        {children}
      </div>
    );
  },
);

ProgressBar.displayName = 'Progress.Bar';
